import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BaseService } from 'cre-ng';

import {
  CommitmentChoiceDto,
  CompetitionResultDto,
  DriverCompetitionDetailDto,
  DriverLeadTotalsDto,
  DriverTeamInformation,
  FleetCompetitionDetailDto,
  FleetLogoDto,
  FleetWeeklyDto,
  LeagueRankingDto,
  RecordingsDto,
  TeamChartInformation,
  TeamLeadMeasure,
  Tractor,
} from '@app/core/driver-wig.model';
import { IDriverWigService } from '@app/core/services/interface/IDriverWigService';
import { DriverCommitmentConfiguration, CommitmentStatus } from '@app/shared/shared.models';
import { Daily5Model } from '@app/wig-daily5-form/wig-daily5-form.model';

@Injectable()
export class DriverWigService extends BaseService implements IDriverWigService {
  constructor(protected httpClient: HttpClient) {
    super({ serviceApiDomain: window.__env.ngSettings.serviceApiDomain }, httpClient);
  }

  creditEligibleCall(
    driverId: string,
    fleetWeeklyId: number,
    driverWeeklyId: number
  ): Promise<number> {
    let httpParams = new HttpParams();

    httpParams = httpParams.append('driverCode', driverId);
    httpParams = httpParams.append('fleetWeekly', fleetWeeklyId.toString());
    httpParams = httpParams.append('driverWeekly', driverWeeklyId.toString());
    const httpHeaders = this.getHttpHeaders();
    const requestOptions = { headers: httpHeaders, params: httpParams };
    return this.httpClient
      .post<number>(this.serviceUrl + '/driverwig/creditcall', null, requestOptions)
      .toPromise();
  }

  creditEligibleRecording(driverId: string, driverWeeklyId: number): Promise<number> {
    let httpParams = new HttpParams();

    httpParams = httpParams.append('driverCode', driverId);
    httpParams = httpParams.append('driverWeekly', driverWeeklyId.toString());
    const httpHeaders = this.getHttpHeaders();
    const requestOptions = { headers: httpHeaders, params: httpParams };
    return this.httpClient
      .post<number>(this.serviceUrl + '/driverwig/creditrecording', null, requestOptions)
      .toPromise();
  }

  getCalledConference(driverId: string, weekStartDate: Date): Promise<boolean> {
    let httpParams = new HttpParams();

    httpParams = httpParams.append('week', weekStartDate.toString());
    const httpHeaders = this.getHttpHeaders();
    const requestOptions = { headers: httpHeaders, params: httpParams };
    return this.httpClient
      .get<boolean>(this.serviceUrl + `/driverwig/${driverId}/calledConference,`, requestOptions)
      .toPromise();
  }

  getCommitmentChoices(driverCode: string): Promise<CommitmentChoiceDto[]> {
    const httpHeaders = this.getHttpHeaders();
    const requestOptions = { headers: httpHeaders };
    return this.httpClient
      .get<CommitmentChoiceDto[]>(
        this.serviceUrl + `/driverwig/commitmentChoices/${driverCode}`,
        requestOptions
      )
      .toPromise();
  }

  getCommitmentConfiguration(driverCode: string): Promise<DriverCommitmentConfiguration[]> {
    const httpHeaders = this.getHttpHeaders();
    const requestOptions = { headers: httpHeaders };
    return this.httpClient
      .get<DriverCommitmentConfiguration[]>(
        this.serviceUrl + `/driverwig/commitmentConfiguration/${driverCode}`,
        requestOptions
      )
      .toPromise();
  }

  getCommitmentStatus(driverCode: string): Promise<CommitmentStatus[]> {
    const httpHeaders = this.getHttpHeaders();
    const requestOptions = { headers: httpHeaders };
    return this.httpClient
      .get<CommitmentStatus[]>(
        this.serviceUrl + `/driverwig/commitmentStatus/${driverCode}`,
        requestOptions
      )
      .toPromise();
  }

  getDriverCompetitionDetails(driverId: string): Promise<DriverCompetitionDetailDto[]> {
    const httpHeaders = this.getHttpHeaders();
    const requestOptions = { headers: httpHeaders };
    return this.httpClient
      .get<DriverCompetitionDetailDto[]>(
        this.serviceUrl + `/driverwig/driverCompetitionDetails/${driverId}`,
        requestOptions
      )
      .toPromise();
  }

  getDriverWeeklyLeadTotals(driverId: string, weekStartDate: Date): Promise<DriverLeadTotalsDto[]> {
    let httpParams = new HttpParams();

    httpParams = httpParams.append('weekStart', weekStartDate.toString());
    const httpHeaders = this.getHttpHeaders();
    const requestOptions = { headers: httpHeaders, params: httpParams };
    return this.httpClient
      .get<DriverLeadTotalsDto[]>(
        this.serviceUrl + `/driverwig/${driverId}/driverWeeklyLeadTotals`,
        requestOptions
      )
      .toPromise();
  }

  getEndPoint(): string {
    return '/driverwig/api/v1';
  }

  getFleetCompetitionDetails(fleet: string): Promise<FleetCompetitionDetailDto[]> {
    const httpHeaders = this.getHttpHeaders();
    const requestOptions = { headers: httpHeaders };
    return this.httpClient
      .get<FleetCompetitionDetailDto[]>(
        this.serviceUrl + `/driverwig/fleetCompetitionDetails/${fleet}`,
        requestOptions
      )
      .toPromise();
  }

  getFleetCompetitionResults(fleet: string, weekStartDate?: Date): Promise<CompetitionResultDto> {
    let httpParams = new HttpParams();

    if (weekStartDate) {
      httpParams = httpParams.append('weekStart', weekStartDate.toString());
    }
    const httpHeaders = this.getHttpHeaders();
    const requestOptions = { headers: httpHeaders, params: httpParams };
    return this.httpClient
      .get<CompetitionResultDto>(
        this.serviceUrl + `/driverwig/${fleet}/competitionResults`,
        requestOptions
      )
      .toPromise();
  }

  getFleetLeagueRankings(fleet: string): Promise<LeagueRankingDto> {
    const httpHeaders = this.getHttpHeaders();
    const requestOptions = { headers: httpHeaders };
    return this.httpClient
      .get<LeagueRankingDto>(
        this.serviceUrl + `/driverwig/${fleet}/fleetLeagueRankings`,
        requestOptions
      )
      .toPromise();
  }

  getFleetLogo(fleet: string): Promise<FleetLogoDto> {
    const httpHeaders = this.getHttpHeaders();
    const requestOptions = { headers: httpHeaders };
    return this.httpClient
      .get<FleetLogoDto>(this.serviceUrl + `/driverwig/${fleet}/fleetLogo`, requestOptions)
      .toPromise();
  }

  getFleetsCompetitionResults(
    fleets: string[],
    weekStartDate?: Date
  ): Promise<CompetitionResultDto[]> {
    let httpParams = new HttpParams();

    fleets.forEach((fleet) => {
      httpParams = httpParams.append('fleets', fleet);
    });

    if (weekStartDate) {
      httpParams = httpParams.append('weekStart', weekStartDate.toString());
    }
    const httpHeaders = this.getHttpHeaders();
    const requestOptions = { headers: httpHeaders, params: httpParams };
    return this.httpClient
      .get<CompetitionResultDto[]>(
        this.serviceUrl + '/driverwig/competitionResultsForFleets',
        requestOptions
      )
      .toPromise();
  }

  getFleetWeekly(driverId: string): Promise<FleetWeeklyDto> {
    const httpHeaders = this.getHttpHeaders();
    const requestOptions = { headers: httpHeaders };
    return this.httpClient
      .get<FleetWeeklyDto>(this.serviceUrl + `/driverwig/${driverId}/fleetWeekly`, requestOptions)
      .toPromise();
  }

  getLatestRecordings(driverId: string): Promise<RecordingsDto[]> {
    const httpHeaders = this.getHttpHeaders();
    const requestOptions = { headers: httpHeaders };
    return this.httpClient
      .get<RecordingsDto[]>(
        this.serviceUrl + `/driverwig/latestrecordings?driverCode=${driverId}`,
        requestOptions
      )
      .toPromise();
  }

  getTractorsByDriverId(driverId: string): Promise<Tractor[]> {
    const httpHeaders = this.getHttpHeaders();
    const requestOptions = { headers: httpHeaders };
    return this.httpClient
      .get<Tractor[]>(this.serviceUrl + `/driverwig/${driverId}/tractors`, requestOptions)
      .toPromise();
  }

  postCommitmentChoice(driverCode: string, lead1: number, lead2: number): Promise<string> {
    let httpParams = new HttpParams();

    httpParams = httpParams.append('lead1', lead1.toString());
    httpParams = httpParams.append('lead2', lead2.toString());
    const httpHeaders = this.getHttpHeaders();
    return this.httpClient
      .post<string>(this.serviceUrl + `/driverwig/commitmentChoice/${driverCode}`, null, {
        params: httpParams,
        headers: httpHeaders,
      })
      .toPromise();
  }

  saveDaily5(daily5: Daily5Model): Promise<string> {
    const httpHeaders = this.getHttpHeaders();
    return this.httpClient
      .request('POST', this.serviceUrl + '/driverwig/daily5Response', {
        body: daily5.toJSON(),
        responseType: 'text',
        headers: httpHeaders,
      })
      .toPromise();
  }

  saveRecordingClicked(driverId: string, fleetWeeklyId: number): Promise<number> {
    let httpParams = new HttpParams();

    httpParams = httpParams.append('driverCode', driverId);
    httpParams = httpParams.append('fleetWeekly', fleetWeeklyId.toString());
    const httpHeaders = this.getHttpHeaders();
    const requestOptions = { headers: httpHeaders, params: httpParams };
    return this.httpClient
      .post<number>(this.serviceUrl + '/driverwig/saverecordingclicked', null, requestOptions)
      .toPromise();
  }

  /* Team endpoints */
  getDriverTeamInformation(driverCode: string): Promise<DriverTeamInformation> {
    const timeOfTheCall = new Date().getTime();
    const httpHeaders = this.getHttpHeaders();
    const requestOptions = { headers: httpHeaders };
    return this.httpClient
      .get<DriverTeamInformation>(
        this.serviceUrl + `/team/driverteaminformation?driverCode=${driverCode}&${timeOfTheCall}`,
        requestOptions
      )
      .toPromise();
  }

  getDriverLeadMeasure(driverCode: string): Promise<TeamLeadMeasure> {
    const timeOfTheCall = new Date().getTime();
    const httpHeaders = this.getHttpHeaders();
    const requestOptions = { headers: httpHeaders };
    return this.httpClient
      .get<TeamLeadMeasure>(
        this.serviceUrl + `/team/driverteamleadmeasure?driverCode=${driverCode}&${timeOfTheCall}`,
        requestOptions
      )
      .toPromise();
  }

  getDriverTeamBattleChart(driverCode: string): Promise<TeamChartInformation> {
    const timeOfTheCall = new Date().getTime();
    const httpHeaders = this.getHttpHeaders();
    const requestOptions = { headers: httpHeaders };
    return this.httpClient
      .get<TeamChartInformation>(
        this.serviceUrl +
          `/team/driverchartbattleinformation?driverCode=${driverCode}&${timeOfTheCall}`,
        requestOptions
      )
      .toPromise();
  }
}
